import { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/layout';
import { Link } from 'react-router-dom';
import { useAsync } from 'react-async';
import { TimesheetStatus, ChangeAffectOptions } from '../../common/constants';
import { getYearsBetween, sortAlphabetically, convertMinutesToHoursAndMinutes, generateOptions } from '../../common/utils';
import React from 'react';
import DataTable from '../../components/dataTable';
import SubmitButton from '../../components/submitButton';
import { getDisciplines } from '../../api/services/disciplineService';
import { getDistrictOptions } from '../../api/services/districtService';
import { getSchoolsByDistrict } from '../../api/services/schoolService';
import { searchTimesheetSummaries } from '../../api/services/timesheetService';
import { getDivisionOptions } from '../../api/services/divisionService';
import { getDistrictsByDivisionId } from '../../api/services/therapistService';
import { batchUpdateTimesheetStatuses, getFlags } from '../../api/services/timesheetProofingService';
import { hasDecreasedHours } from '../../api/services/timesheetApprovalProofingService';
import ProofingRule from '../../api/models/proofingRule';
import TimesheetEntryFlag from '../../api/models/timesheetEntryFlag';
import TimesheetFlag from '../../api/models/timesheetFlag';
import { TimesheetSummarySearch } from '../../api/models/timesheetSummarySearch';
import moment from 'moment';
import { getProgramsByDistrict } from '../../api/services/programService';
import { FieldInput } from '../../components/fieldInput';
import Select from 'react-select';
import { option } from '../../api/models/option';
import { graySelectStyles } from '../../common/selectStyles';
import DatePicker from 'react-datepicker';
import CustomAlert from '../../components/alert/commonAlert';

/**
 * Fetch functions
 */
const fetchDisciplines = async () => getDisciplines();
const fetchDistricts = async () => getDistrictOptions();
const fetchDivisions = async () => getDivisionOptions();
const fetchFlags = async () => getFlags();
const batchApprove = async (args: any[]) => await batchUpdateTimesheetStatuses(args[0], args[1]);
const doSearch = async (args: any) => {
  return searchTimesheetSummaries(args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7], args[8], args[9]);
}

const now = new Date();

const getCurrentWeekDates = () => {

  const today = new Date();
  const currentDate = today.getDate();
  const currentDay = today.getDay();

  const mondayDate = new Date(today);
  mondayDate.setDate(currentDate - (currentDay - 1));

  const sundayDate = new Date(mondayDate);
  sundayDate.setDate(mondayDate.getDate() + 6);

  return {
    monday: mondayDate,
    sunday: sundayDate,
  };
};

/**
 * Component
 */
const ProofingSummarySearch = () => {
  const columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        width: '30px',
        className: 'block mr-4 mt-2',
        disableSortBy: true,
        autoResetSortBy: false,
        Header: (props: any) => <></>,
        Cell: ({ value, column, row }: any) => {
          const t: TimesheetSummarySearch = row.original;

          return t.timesheetStatusId == TimesheetStatus.Submitted ?
            <input
              id={`entry_`}
              className="datatable-checkbox"
              type='checkbox'
              value={t.timesheetId}
              onClick={(e: any) => {
                if (e.target.checked) {
                  setSelected(current => [...current, t.timesheetId!]);
                } else {
                  setSelected(current => [...current.filter(s => s !== t.timesheetId)]);
                }

                e.stopPropagation();
              }}
            /> : <></>
        },
      },
      {
        Header: 'Proofing Page',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return `${t.therapistName}`;
        },
        Cell: (row: any) => {
          const t: TimesheetSummarySearch = row.row.original;
          return t.timesheetStatusId === 0 ? t.therapistName : <Link key={`therapist-name-${t.therapistName}`} className={`therapist-name-${t.therapistName}`} to={`/proofing/week/${t.timesheetWeekId}`}>
            {`${t.therapistName}`}
          </Link>;
        }
      },
      {
        Header: 'Timesheet',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return t.timesheetStatusId === 0 ? "N/A" : <Link to={`/admin/timesheetSearch/${t.timesheetId}`}>Review</Link>;
        },
      },
      {
        Header: 'Discipline',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return t.discipline;
        },
      },
      {
        Header: 'Division',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return t.divisionName;
        },
      },
      {
        Header: 'District',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return t.districtName;
        },
      },
      {
        Header: 'Program',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return `${t?.programName ?? ''}`;
        }
      },
      {
        Header: 'Start Date',
        accessor: (u: any) => {
          return `${u?.startDate && moment(u?.startDate).isValid() ? moment(u?.startDate).format('MM/DD/YYYY') : ''}`;
        }
      },
      {
        Header: 'End Date',
        accessor: (u: any) => {
          return `${u?.endDate && moment(u?.endDate).isValid() && u.endDate !== '0001-01-01T00:00:00' ? moment(u?.endDate).format('MM/DD/YYYY') : moment(u?.startDate).add(1, 'month').subtract(1, 'day').format('MM/DD/YYYY')}`;
        }
      },
      {
        Header: 'Status',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return t.timesheetStatusName;
        },
      },
      {
        Header: 'Total Time',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return `${t.total} ${convertMinutesToHoursAndMinutes(t.total)}`;
        },
        Cell: (row: any): any => convertMinutesToHoursAndMinutes(row.row.original.total)
      },
      {
        Header: 'Issues',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          if (t.timesheetFlags?.some(y => y.proofingRule && y.proofingRule.name && y.proofingRule.name !== '') ||
            t.entryFlags?.some(y => y.proofingRule && y.proofingRule.name && y.proofingRule.name !== '')) {
            const timesheetFlags: TimesheetFlag[] = [];
            const entryFlags: TimesheetEntryFlag[] = [];
            return <ul className="list-disc">
              {t.timesheetFlags?.map(x => {
                if (x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '' && timesheetFlags?.filter(f => f.proofingRuleId === x.proofingRuleId).length === 0) {
                  timesheetFlags.push(x);
                  return <li key={`issue-${x.timesheetId}-${x.timesheetWeekId}-timesheet-flag-${x.dateFlagged.toString()}-${x.proofingRuleId}`}>
                    {x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '' ? `${x.proofingRule.name}` : ''}
                  </li>
                }

                return null;
              }
              )}

              {t.entryFlags.map(flag => {
                if (flag.proofingRule && flag.proofingRule.name && flag.proofingRule.name !== '' && entryFlags?.filter(x => x.proofingRuleId === flag.proofingRuleId).length === 0) {
                  entryFlags.push(flag);
                  return <li key={`issue-${t.timesheetId}-${t.timesheetWeekId}-entry-flag-${flag.dateFlagged.toString()}-${flag.proofingRuleId}`}>
                    {flag.proofingRule.name ?? 'N/A'}
                  </li>
                }

                return null;
              }
              )}
            </ul>;
          }
          return 'N/A';
        },
      },
      {
        Header: 'Feedback',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          if ((t.feedback && t.feedback !== '') ||
            t.timesheetFlags?.some(y => y.feedback && y.feedback !== '') ||
            t.entryFlags?.some(y => (y.feedback && y.feedback !== '') || y.flagNote && y.flagNote !== '')) {
            return <ul className="list-disc">
              {/* Feedback */}
              {t.feedback && t.feedback !== '' ? <li key={`feedback-${t.timesheetId}-${t.timesheetWeekId}-timesheet-feedback-${t.feedback.replace(/\ /gm, '')}`}>{t.feedback}</li> : null}

              {/* Timesheet Flags */}
              {t.timesheetFlags?.map(x => {
                if (x.feedback && x.feedback !== '') {
                  return <li key={`feedback-${x.timesheetId}-${x.timesheetWeekId}-timesheet-flag-${x.dateFlagged.toString()}-${x.proofingRuleId}`}>{x.feedback}</li>
                }
              })}

              {t.entryFlags?.map(x => {
                if (x.feedback && x.feedback !== '') {
                  return <li key={`feedback-${t.timesheetId}-${t.timesheetWeekId}-entry-flag-${x.dateFlagged.toString()}-${x.proofingRuleId}`}>{x.feedback}</li>
                }

                if (x.flagNote && x.flagNote !== '') {
                  return <li key={`flagnote-${t.timesheetId}-${t.timesheetWeekId}-entry-flag-${x.dateFlagged.toString()}-${x.proofingRuleId}`}>{x.flagNote}</li>
                }
              })}
            </ul>
          }

          return 'N/A';
        }
      },
      {
        id: 'proofingPage',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          return `${t.therapistName}`;
        },
        className: 'hidden'
      },
      {
        id: 'allFlags',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          let flags = '';
          t.timesheetFlags?.map(x => {
            if (x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '') {
              flags += `${x.proofingRule.name} `;
            }
          })

          t.entryFlags?.map(x => {
            if (x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '') {
              flags += `${x.proofingRule.name} `;
            }
          })

          return flags;
        },
        className: 'hidden'
      },
      {
        id: 'feedback',
        accessor: (s: any) => {
          const t: TimesheetSummarySearch = s;
          let feedback = '';
          t.timesheetFlags?.map(x => {
            if (x.feedback && x.feedback !== '') {
              feedback += `${x.feedback}, `;
            }
          })

          t.entryFlags.map(x => {
            if (x.feedback && x.feedback !== '') {
              feedback += `${x.feedback}, `;
            }

            if (x.flagNote && x.flagNote !== '') {
              feedback += `${x.flagNote}, `;
            }
          })

          return feedback;
        },
        className: 'hidden'
      }
    ],
    []);

  //startDate and endDate (required)
  const { monday, sunday } = getCurrentWeekDates();
  const [startDate, setStartDate] = useState<Date | null>(monday);
  const [endDate, setEndDate] = useState<Date | null>(sunday);
  const [therapistName, setName] = useState('');

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>();


  //Timesheet hooks  
  const [flags, setFlags] = useState<ProofingRule[]>([]);
  const [disciplineOptions, setDisciplineOptions] = useState<option[]>([]);
  const [discipline, setDiscipline] = useState<number>(-1);
  const [flagOptions, setFlagOptions] = useState<option[]>([]);
  const [flag, setFlag] = useState<number>(-1);
  const [schoolOptions, setSchoolOptions] = useState<option[]>([]);
  const [school, setSchool] = useState<number>(-1);
  const [districtOptions, setDistrictOptions] = useState<option[]>([]);
  const [districtId, setDistrictId] = useState<number>(-1);
  const [programId, setProgramId] = useState<number>(-1);
  const [divisionOptions, setDivisionOptions] = useState<option[]>([]);
  const [programOptions, setProgramOptions] = useState<option[]>([]);
  const [division, setDivision] = useState<number>(-1);
  const [status, setStatus] = useState<number>(-1);
  const [statusOptions, setStatusOptions] = useState<option[]>([]);

  const [searchResults, setSearchResults] = useState<TimesheetSummarySearch[]>([]);
  const [isSelect, setIsSelect] = useState<boolean>(false);
  const [selected, setSelected] = useState<number[]>([]);

  // Change Affect dropdown state
  const [changeAffect, setChangeAffect] = useState<string>("");
  const [showChangeAffect, setShowChangeAffect] = useState<boolean>(false);
  const [checkingChangeAffect, setCheckingChangeAffect] = useState<boolean>(false);

  useEffect(() => {
    const options: option[] = [];
    Object.entries(TimesheetStatus).map(status => {
      if (Number.isInteger(status[1])) {
        options.push({ value: Number(status[1]), label: status[0].replace('_', '').replace(/([A-Z])/g, ' $1') });
      }
    });

    setStatusOptions(sortAlphabetically(options, 'label'));
  }, []);

  //Get the districts
  const flasgAsync = useAsync({
    promiseFn: fetchFlags,
    onResolve: (u) => {
      let options: option[] = flagOptions;

      //set options           
      (u.data.map((g) => options.push({ value: g.proofingRuleId ?? -1, label: g.name })));
      options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setFlagOptions(options);
    },
  });

  //Generate our years options
  const yearsOptions: option[] = useMemo(() => {
    const options: option[] = [];
    getYearsBetween(2000, new Date().getFullYear()).map(y => options.push({ label: y.toString(), value: y }));
    return options;
  }, []);

  //Get the disciplines
  const disciplinesAsync = useAsync({
    promiseFn: fetchDisciplines,
    onResolve: (u) => {
      let options: option[] = disciplineOptions;

      //set options           
      (u.data.disciplines.map((g) => options.push({ value: g.disciplineId ?? -1, label: g.name })));
      options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setDisciplineOptions(options);
    },
  });

  const districtsData = useAsync({
    promiseFn: fetchDistricts,
    onResolve: (u) => {
      let options: option[] = districtOptions;
      u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
      setDistrictOptions(options);
    },
  });

  const divisionAsync = useAsync({
    promiseFn: fetchDivisions,
    onResolve: (u) => {
      let options: option[] = divisionOptions;
      u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
      options.shift();
      setDivisionOptions(options);
    },
  });

  /**
   * Whenever district changes, update the list of schools
   */
  useEffect(() => {
    updatePrograms(districtId, division);

    if (districtId != null) {
      updateSchools(districtId);
    }
  }, [districtId]);

  /**
  * Functions to update the select boxes
  */
  const updateSchools = async (district: number) => {
    const response = await getSchoolsByDistrict(district);

    //If we don't have any schools, inform the user
    if (response.data.schools.length === 0) {
      setSchoolOptions([{ value: -1, label: '' }]);
      return true;
    }

    const schools = generateOptions(response.data.schools, 'schoolId', 'name', true);
    setSchoolOptions(schools);
  }

  const updatePrograms = async (district: number, division: number) => {
    const response = await getProgramsByDistrict(district, division);

    if (response.data.programs.length === 0) {
      setProgramOptions([{ value: -1, label: '' }]);
      return true;
    }

    const programs = generateOptions(response.data.programs, 'programId', 'name', true);
    setProgramOptions(programs);
  }

  /**
   * Whenever district changes, update the list of schools
   */
  useEffect(() => {
    if (division > 0) updateDistricts(division);

    updatePrograms(districtId, division);
  }, [division]);

  useEffect(() => {
    let requestTimeString = localStorage.getItem("timesheetSummaryTime");
    let requestTime = moment(requestTimeString);

    if (requestTime && moment().isAfter(requestTime.add(10, 'minutes'))) {
      localStorage.removeItem("timesheetSummaryData");
      localStorage.removeItem("timesheetSummaryTime");
      localStorage.removeItem("timesheetSummaryFilter")
      return;
    }

    let dataString = localStorage.getItem("timesheetSummaryData");

    if (dataString) {
      let filterString = localStorage.getItem("timesheetSummaryFilter");
      if (filterString) {
        let filterData = JSON.parse(filterString);
        console.log(filterData)
        setName(filterData.therapistName);
        setStartDate(filterData.startDate ? new Date(filterData.startDate) : null);
        setEndDate(filterData.endDate ? new Date(filterData.endDate) : null);
        setStatus(filterData.status);
        setDistrictId(filterData.district);
        setSchool(filterData.school);
        setDiscipline(filterData.discipline);
        setFlag(filterData.flag);
        setDivision(filterData.division);
        setProgramId(filterData.program);
      }

      let data = JSON.parse(dataString);
      setSearchResults(data);
    }
  }, [])

  /**
  * Functions to update the select boxes
  */
  const updateDistricts = async (divisionId: number) => {
    const response = await getDistrictsByDivisionId(divisionId);

    //If we don't have any schools, inform the user
    if (response.data.districts.length === 0) {
      setDistrictOptions([{ value: -1, label: '' }]);
      return true;
    }

    const districts = generateOptions(response.data.districts, 'districtId', 'name', true);
    setDistrictOptions(districts);
  }

  /**
   * Primary search async function
   */
  const handleSearchAsync = useAsync({
    deferFn: doSearch,
    onResolve: (r) => {
      setSearchResults(r.data)

      let dataString = JSON.stringify(r.data);
      let currentDateTime = moment().toString();

      localStorage.setItem("timesheetSummaryData", dataString)
      localStorage.setItem("timesheetSummaryTime", currentDateTime)
      setFilterData();
    }
  });

  const setFilterData = () => {
    let filterData = {
      therapistName: therapistName,
      startDate: startDate,
      endDate: endDate,
      status: status,
      district: districtId,
      school: school,
      discipline: discipline,
      flag: flag,
      division: division,
      program: programId
    }

    console.log(filterData)
    let filterDataString = JSON.stringify(filterData);
    localStorage.setItem("timesheetSummaryFilter", filterDataString);
  }

  const resetFilter = () => {
    setName('');
    const dates = getCurrentWeekDates();
    setStartDate(dates.monday);
    setEndDate(dates.sunday);
    setStatus(-1);
    setDistrictId(-1);
    setSchool(-1);
    setDiscipline(-1);
    setFlag(-1);
    setDivision(-1);
    setProgramId(-1);

    setFilterData();
  }

  /**
   * This function selects or de-selects all checkboxes in a datatable
   *
   * @return {void} This function does not return anything.
   */
  const selectAll = (): void => {
    //Get all checkboxes
    const checkboxes = [...document.querySelectorAll('.datatable-checkbox')];

    //Select or de-select based on the previous state
    //Set a timeout for 0 seconds. This will place this call next in the call stack
    //which will prevent the UI from freezing while the item is being processed
    checkboxes.map(box => setTimeout(() => (box as HTMLInputElement).checked = !isSelect, 0));

    const newSelected: number[] = [];

    //Loop through the days and gets all the weeks
    if (!isSelect) {
      searchResults.map(result => result.timesheetId && result.timesheetId > 0 && result.timesheetStatusId === TimesheetStatus.Submitted ? newSelected.push(result.timesheetId) : null);
    }

    setSelected([...newSelected]);
    setIsSelect(current => !current);
  }

  const unSelectAll = (): void => {
    //Get all checkboxes
    const checkboxes = [...document.querySelectorAll('.datatable-checkbox')];

    //select or de-select based on the previous state
    checkboxes.map(box => setTimeout(() => (box as HTMLInputElement).checked = false, 0));
    setSelected([]);
    setIsSelect(false);
  }

  /**
     * When the page size changes on the table and the select all button
     * is enabled, then check all the new checkboxes as well
     * @param pageSize number
     */
  const onChangePageSize = (pageSize: number) => {
    if (isSelect) {
      [...document.querySelectorAll('.datatable-checkbox')]
        .map((checkbox: any) => setTimeout(() => !checkbox.checked ? checkbox.click() : true, 0));
    }
  }

  // When selected timesheets change, check if any has only decreased hours
  useEffect(() => {
    if (selected.length > 0) {
      setCheckingChangeAffect(true);
      Promise.all(
        selected.map(id =>
          hasDecreasedHours(id)
            .then(r => r.data === true)
            .catch(() => false)
        )
      ).then(results => {
        setShowChangeAffect(results.some(r => r));
        if (!results.some(r => r)) setChangeAffect("");
      }).finally(() => setCheckingChangeAffect(false));
    } else {
      setShowChangeAffect(false);
      setChangeAffect("");
    }
  }, [selected]);

  const approvalHandler = useAsync({
    deferFn: batchApprove,
    onResolve: () => {
      setShowChangeAffect(false);
      setChangeAffect("");
      handleSearchAsync.run(therapistName, startDate, endDate, districtId, school, discipline, flag, division, status, programId);
    }
  });

  /**
   * JSX element for below the filter element on a datatable
   */
  const approvalBar = <div className="pb-0.5 mt-4 mb-0.5 approval-bar flex items-center">
    <button
      onClick={selectAll}
      className='inline-flex justify-center px-4 py-2 text-base text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsblue-normal hover:bg-ptsblue-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
    >{isSelect ? 'Uns' : 'S'}elect All</button>
    {showChangeAffect && (
      <div className='inline-flex items-center ml-3' style={{ minWidth: '200px', verticalAlign: 'middle' }}>
        <label className='text-sm font-bold mr-2 whitespace-nowrap'>Change Affect</label>
        <Select
          className='inline-block w-full'
          styles={graySelectStyles}
          isMulti={false}
          options={ChangeAffectOptions as any}
          isClearable={true}
          placeholder='Select Change Affect...'
          onChange={(e: any | null) => {
            setChangeAffect(e ? e.value : '');
          }}
        />
      </div>
    )}
    <button
      type="button"
      disabled={selected.length === 0 || checkingChangeAffect || (showChangeAffect && !changeAffect)}
      onClick={() => {
        approvalHandler.run(selected, showChangeAffect && changeAffect ? changeAffect : undefined);
        unSelectAll();
      }}
      className='primary button sm:ml-3 sm:w-auto sm:text-sm'
    >Approve</button>
  </div>;

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  return (
    <Layout
      isLoading={
        districtsData.isPending ||
        disciplinesAsync.isPending ||
        divisionAsync.isPending ||
        flasgAsync.isPending ||
        handleSearchAsync.isPending ||
        approvalHandler.isPending
      }>
      <div>
        <div className='w-full mt-2'>
          <div className='z-0 px-4 py-5 bg-white sm:p-6'>
            <div className='z-0 grid grid-cols-6 gap-6 mb-4'>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                  <FieldInput
                    type='text'
                    value={therapistName}
                    rounded='sm'
                    placeholder=''
                    label={`Therapist`}
                    onChange={(e) => { setName(e.target.value); }}
                    icon={'search'}
                  />
                </div>
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Start Date<span className="text-red-500">*</span></label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    if (date) {
                      const maxEndDate = new Date(date as Date);
                      maxEndDate.setDate(maxEndDate.getDate() + 90); // Set max end date to 90 days after new start date

                      // Check if current end date is within the new valid range
                      if (endDate && endDate > maxEndDate) {
                        setAlertMessage("The selected end date exceeds the 90-day limit from the new start date. Please choose a valid end date.");
                        setShowAlert(true)
                        setEndDate(null);
                      }
                    }
                    setStartDate(date as Date)

                  }}
                  selectsStart
                  startDate={startDate}
                  maxDate={endDate}
                  endDate={endDate}
                  className="border p-2 rounded"
                  placeholderText="Select a start date"
                />

              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>End Date<span className="text-red-500">*</span></label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    if (startDate && date) {
                      const maxEndDate = new Date(startDate);
                      maxEndDate.setDate(maxEndDate.getDate() + 90); // Add 90 days to start date

                      if (date > maxEndDate) {
                        setAlertMessage("End date cannot exceed 90 days from the start date.");
                        setShowAlert(true);
                        return;
                      }
                    }
                    setEndDate(date as Date)
                  }}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate} // Prevents selecting an end date before the start date
                  className="border p-2 rounded"
                  placeholderText="Select an end date"
                />
              </div>
            </div>
            {showAlert && (
              <CustomAlert
                message={alertMessage!}
                onClose={handleCloseAlert}
              />
            )}
            <div className='z-0 grid grid-cols-6 gap-6'>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Status</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: statusOptions.find(x => x.value === status)?.label, value: status } as option}
                  name='test'
                  options={statusOptions ?? []}
                  placeholder={`Select Status`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setStatus(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>District</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: districtOptions.find(x => x.value === districtId)?.label, value: districtId } as option}
                  name='test'
                  options={districtOptions ?? []}
                  placeholder={`Select District`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setDistrictId(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>School</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: schoolOptions.find(x => x.value === school)?.label, value: school } as option}
                  name='test'
                  options={schoolOptions ?? []}
                  placeholder={`Select School`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setSchool(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Discipline</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: disciplineOptions.find(x => x.value === discipline)?.label, value: discipline } as option}
                  name='test'
                  options={disciplineOptions ?? []}
                  placeholder={`Select Discipline`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setDiscipline(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Flag</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: flagOptions.find(x => x.value === flag)?.label, value: flag } as option}
                  name='test'
                  options={flagOptions ?? []}
                  placeholder={`Select Flag`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setFlag(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Division</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: divisionOptions.find(x => x.value === division)?.label, value: division } as option}
                  name='test'
                  options={divisionOptions ?? []}
                  placeholder={`Select Division`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setDivision(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Program</label>
                <Select
                  className='block mt-1 primary'
                  value={{ label: programOptions.find(x => x.value === programId)?.label, value: programId } as option}
                  name='test'
                  options={programOptions ?? []}
                  placeholder={`Select Program`}
                  onChange={(e: any | null) => {
                    //Null exception handling
                    e = (e !== null) ? e : { label: '', value: -1 };
                    setProgramId(Number(e.value));
                  }}
                  isSearchable={false}
                  styles={graySelectStyles}
                />
              </div>
            </div>
          </div>
          <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
            <SubmitButton
              text='Search'
              className='mr-5 primary button'
              onClick={() => handleSearchAsync.run(therapistName, startDate, endDate, districtId, school, discipline, flag, division, status, programId)}
              disabled={
                startDate == undefined || startDate == null ||
                endDate == undefined || endDate == null
              }
            />

            <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
              <button
                className='mr-5 secondary'
                onClick={() => { resetFilter(); }}
              >
                Reset filter
              </button>
            </div>
          </div>
          <div className='p-3 mt-5 bg-white rounded-lg shadow'>
            <div className='mt-5'></div>
            <DataTable
              columns={columns}
              data={searchResults}
              showPagination={true}
              postFilter={approvalBar}
              getRowProps={(row: any) => ({
                className: row.original.isMyMessage
                  ? 'cursor-pointer hover:bg-purple-300 bg-purple-200'
                  : 'cursor-pointer hover:bg-gray-100',
                onClick: () => { }
                //history.push(`/admin/districts/${row.values.districtId}`),
              })}
              onChangePageSize={onChangePageSize}
            />
            {/* Week (Timesheet) Display */}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ProofingSummarySearch;

